import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  LocationOn as LocationIcon,
  Pets as PetsIcon,
  Favorite as FavoriteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Schedule as ScheduleIcon,
  Assignment as ApplicationIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '@config/api';

const API_URL = API_BASE_URL;

interface Shelter {
  id: string;
  name: string;
  description?: string;
  city?: string;
  county?: string;
  phone?: string;
  email?: string;
  current_occupancy?: number;
  capacity?: number;
}

const SheltersPage: React.FC = () => {
  const [shelters, setShelters] = useState<Shelter[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    city: '',
    county: '',
    species: '',
    ageRange: '',
    size: '',
    compatibility: '',
    q: ''
  });

  useEffect(() => {
    loadShelters();
  }, [page, filters]);

  const loadShelters = async () => {
    try {
      setLoading(true);
      // Endpoint public pentru clienți (doar adaposturi active)
      const response = await axios.get(`${API_URL}/shelters`, {
        params: {
          ...filters,
          status: 'active',
          page,
          pageSize: 12
        }
      });
      
      setShelters(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error: any) {
      console.error('Eroare la încărcarea adaposturilor:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <FavoriteIcon sx={{ fontSize: 40, color: 'error.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Adaposturi & Adopții
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Găsește un prieten credincios din adaposturile partenere
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Filtre */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Caută adapost"
                placeholder="Nume adapost..."
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Oraș"
                value={filters.city}
                onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Județ"
                value={filters.county}
                onChange={(e) => setFilters({ ...filters, county: e.target.value })}
              >
                <MenuItem value="">Toate județele</MenuItem>
                <MenuItem value="Bucuresti">București</MenuItem>
                <MenuItem value="Ilfov">Ilfov</MenuItem>
                <MenuItem value="Cluj">Cluj</MenuItem>
                <MenuItem value="Timis">Timiș</MenuItem>
                {/* Adaugă mai multe județe */}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Lista Adaposturi */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : shelters.length === 0 ? (
        <Alert severity="info">
          Nu am găsit adaposturi care să corespundă criteriilor tale de căutare.
        </Alert>
      ) : (
        <>
          <Grid container spacing={3}>
            {shelters.map((shelter) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={shelter.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardMedia
                    component="div"
                    sx={{
                      height: 200,
                      bgcolor: 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <PetsIcon sx={{ fontSize: 80, color: 'white' }} />
                  </CardMedia>
                  
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {shelter.name}
                    </Typography>
                    
                    {shelter.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {shelter.description}
                      </Typography>
                    )}
                    
                    <Stack spacing={1}>
                      {(shelter.city || shelter.county) && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {shelter.city}, {shelter.county}
                          </Typography>
                        </Stack>
                      )}
                      
                      {shelter.capacity && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PetsIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {shelter.current_occupancy || 0} / {shelter.capacity} animale
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button 
                      fullWidth 
                      variant="contained" 
                      color="primary"
                      href={`/client/shelters/${shelter.id}`}
                      sx={{ mb: 1 }}
                    >
                      Vezi Animalele
                    </Button>
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      color="secondary"
                      href={`/shelters/${shelter.id}`}
                    >
                      Pagina Publică
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Paginare */}
          {total > 12 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={Math.ceil(total / 12)}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default SheltersPage;

